
import * as React from 'react';
import { connectAdvanced, ConnectOptions } from 'react-redux';
import { IItemCache, IItemCacheSelector } from '../../models/IItemCacheObserver';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IItemCacheState } from '../../store/reducers/itemCacheReducer';
import { areShallowDifferent, assign } from '@ms/utilities-objects/lib/index';
import { withItemCacheDemander, IItemCacheDemanderProps } from './withItemCacheDemander';
import { IControlFaceted, CONTROL_HANDLER_CONTEXT, useComposedControlHandlers } from './withControlHandler';
import { IItemCacheStore } from '../../store/createStore';
import { Provider, ReactReduxContextValue } from 'react-redux';
import { IAction } from '../../store/actions/createActionCreator';
import { getItemCacheEvaluator } from '../../models/itemCache/ItemCacheEvaluator';
import { bindItemCacheSelectorToProps } from '../../models/itemCache/bindItemCacheSelectorToProps';
import { useBackgroundTaskHost } from '../backgroundTaskRunner/BackgroundTaskHost';

/**
 * @internal
 */
export const ITEM_CACHE_CONTEXT: React.Context<ReactReduxContextValue<IItemCacheState, IAction>> =
  // tslint:disable-next-line:no-non-null-assertion
  React.createContext<ReactReduxContextValue<IItemCacheState, IAction>>(undefined!);

const CONNECT_OPTIONS: ConnectOptions = {
  context: ITEM_CACHE_CONTEXT
};

/**
 * @internal
 */
export interface IItemCacheReduxProviderProps {
  store: IItemCacheStore;
}

/**
 * @internal
 */
export const ItemCacheReduxProvider: React.ComponentType<IItemCacheReduxProviderProps> =
  class extends React.PureComponent<IItemCacheReduxProviderProps> {
    public render(): JSX.Element {
      return (
        <Provider store={ this.props.store } context={ ITEM_CACHE_CONTEXT }>
          { this.props.children }
        </Provider>
      );
    }
  };

interface IConnectedControlHandlerProps {
  itemCacheConnectorControlHandlers: IControlFaceted;
}

export function connectWithReduxItemCache<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps: IItemCacheSelector<TInjectedProps, TOwnProps>,
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    // A 'back-reference' to the final output component, which can be passed to the `ItemCacheObserver`
    // instances so conditional breakpoints may be used to diagnose the selectors for specific components.
    let debugComponent: IDecoratedComponentType<P, TInjectedProps, TOwnProps>;

    const getOwner = () => debugComponent;

    const WithItemCacheDemander = withItemCacheDemander()(component);

    const mapAdvanced =
      // The final result must be the same object if nothing ultimately changed.
      // Redux relies on this memoization to avoid re-renders.
      // dbounceResult MUST be the outer-most decorator applied here.
      debounceResult(
        withBaseProps(
          debounceProps(
            mergeMappers(
              createMapItemCacheToPropsMapper(mapItemCacheToProps, getOwner),
              createMapItemCacheDispatchToPropsMapper(mapItemCacheDispatchToProps)
            )
          )
        )(WithItemCacheDemander)
      );

    const ConnectAdvanced = connectAdvanced(mapAdvanced, CONNECT_OPTIONS)(WithItemCacheDemander) as {} as React.ComponentType<
      IDecoratedProps<P, TInjectedProps, TOwnProps & IConnectedControlHandlerProps>
    >;

    function ConnectWithRedux(props: IDecoratedProps<P, TInjectedProps, TOwnProps>): JSX.Element {
      const parentControlHandlers = React.useContext(CONTROL_HANDLER_CONTEXT);

      const [childControlHandlers, element] = useBackgroundTaskHost({ scope: 'local' });

      const controlHandlers = useComposedControlHandlers(parentControlHandlers, childControlHandlers);

      return (
        <>
          { element }
          <ConnectAdvanced
            { ...props }
            key='connect'
            itemCacheConnectorControlHandlers={ controlHandlers }
          />
        </>
      );
    }

    debugComponent = ConnectWithRedux;

    assign(ConnectWithRedux, {
      displayName: 'ConnectWithRedux'
    });

    return ConnectWithRedux;
  };
}

export function connectWithReduxDispatch<TInjectedProps, TOwnProps = {}>(
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    const mapAdvanced =
      // The final result must be the same object if nothing ultimately changed.
      // Redux relies on this memoization to avoid re-renders.
      // dbounceResult MUST be the outer-most decorator applied here.
      debounceResult(
        withBaseProps(
          debounceProps(
            createMapItemCacheDispatchToPropsMapper(mapItemCacheDispatchToProps)
          )
        )(component)
      );

    const ConnectWithReduxDispatch = connectAdvanced(mapAdvanced, CONNECT_OPTIONS)(component);

    ConnectWithReduxDispatch.displayName = 'ConnectWithReduxDispatch';

    return ConnectWithReduxDispatch;
  };
}

interface IMemoizedMapper<TInjectedProps, TOwnProps> {
  (dispatch: IItemCacheDispatch): (state: IItemCacheState, props: TOwnProps) => TInjectedProps;
}

/**
 * Decorator which ensures a mapper always includes the original props under the result.
 */
function withBaseProps<TInjectedProps, TOwnProps>(
  mapper: IMemoizedMapper<TInjectedProps, TOwnProps>
): <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ) => IMemoizedMapper<P & TInjectedProps, IDecoratedProps<P, TInjectedProps, TOwnProps>> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IMemoizedMapper<P & TInjectedProps, IDecoratedProps<P, TInjectedProps, TOwnProps>> => {
    return (dispatch: IItemCacheDispatch) => {
      const callback = mapper(dispatch);

      return (state: IItemCacheState, props: IDecoratedProps<P, TInjectedProps, TOwnProps>): P & TInjectedProps => {
        return {
          ...(props as {}),
          ...(callback(state, props) as {})
        } as {} as P & TInjectedProps;
      };
    };
  };
}

/**
 * Decorator which debounces the props passed to a memoized state mapper.
 * A new props object is passed to the mapper only if it differs from the previous props.
 */
function debounceProps<TInjectedProps, TOwnProps>(
  memoizedMapper: IMemoizedMapper<TInjectedProps, TOwnProps>
): IMemoizedMapper<TInjectedProps, TOwnProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousProps: TOwnProps;

    const callback = memoizedMapper(dispatch);

    return (state: IItemCacheState, props: TOwnProps): TInjectedProps => {
      const havePropsChanged = !previousProps || areShallowDifferent(props, previousProps);

      previousProps = props;

      const finalProps = havePropsChanged ? props : previousProps;

      return callback(state, finalProps);
    };
  };
}

/**
 * Decorator which debounces the result output from a mapper.
 * A new result object is produced only if it differs from the previous exection of the mapper.
 */
function debounceResult<TInjectedProps, TOwnProps>(
  memoizedMapper: IMemoizedMapper<TInjectedProps, TOwnProps>
): IMemoizedMapper<TInjectedProps, TOwnProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousResult: TInjectedProps;

    const callback = memoizedMapper(dispatch);

    return (state: IItemCacheState, props: TOwnProps): TInjectedProps => {
      const result = callback(state, props);

      const hasResultChanged = !previousResult || areShallowDifferent(result, previousResult);

      return hasResultChanged ? (previousResult = result) : previousResult;
    };
  };
}

/**
 * Merges two state mappers together, producing a new mapper which takes the props from
 * both and merges the output from both.
 * The second mapper's output overwrites matching props from the first.
 */
function mergeMappers<TInjectedProps1, TInjectedProps2, TOwnProps1, TOwnProps2>(
  firstMapper: IMemoizedMapper<TInjectedProps1, TOwnProps1>,
  secondMapper: IMemoizedMapper<TInjectedProps2, TOwnProps2>
): IMemoizedMapper<TInjectedProps1 & TInjectedProps2, TOwnProps1 & TOwnProps2> {
  return (dispatch: IItemCacheDispatch) => {
    const firstCallback = firstMapper(dispatch);
    const secondCallback = secondMapper(dispatch);

    return (
      state: IItemCacheState,
      props: TOwnProps1 & TOwnProps2
    ): TInjectedProps1 & TInjectedProps2 => {
      return {
        ...(firstCallback(state, props) as {}),
        ...(secondCallback(state, props) as {})
      } as TInjectedProps1 & TInjectedProps2;
    };
  };
}

/**
 * Creates a memoized mapper using a given mapItemCacheDispatchToProps function.
 */
function createMapItemCacheDispatchToPropsMapper<TInjectedProps, TOwnProps = {}>(
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps
): IMemoizedMapper<TInjectedProps, TOwnProps> {
  return (dispatch: IItemCacheDispatch) => {
    let previousProps: TOwnProps | undefined;

    let dispatchResult: TInjectedProps;

    const defaultDispatchResult: {} = {
      itemCacheDispatch: dispatch
    };

    return (state: IItemCacheState, props: TOwnProps): TInjectedProps => {
      const havePropsChanged = !previousProps || areShallowDifferent(previousProps, props);

      previousProps = props;

      if (havePropsChanged) {
        dispatchResult = mapItemCacheDispatchToProps ?
          mapItemCacheDispatchToProps(dispatch, props) :
          defaultDispatchResult as TInjectedProps;
      }

      return dispatchResult;
    };
  };
}

/**
 * Creates a memoized mapper using a given mapItemCacheToProps function.
 */
function createMapItemCacheToPropsMapper<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps: IItemCacheSelector<TInjectedProps, TOwnProps>,
  getOwner: () => ({} | undefined)
): IMemoizedMapper<TInjectedProps & IItemCacheDemanderProps, TOwnProps & IConnectedControlHandlerProps> {
  return (dispatch: IItemCacheDispatch) => {
    // Determine the component type which initialized this memoized selector.
    const owner = {
      component: getOwner()
    };

    let previousFinalProps: TOwnProps | undefined;
    let previousSelector: ((itemCache: IItemCache) => TInjectedProps) | undefined;

    const itemCacheEvaluator = dispatch(getItemCacheEvaluator);

    return (state: IItemCacheState, props: TOwnProps & IConnectedControlHandlerProps): TInjectedProps & IItemCacheDemanderProps => {
      const {
        // Separate the control handlers from the props,
        // so that they can be compared separately.
        itemCacheConnectorControlHandlers: controlHandlers,
        ...finalProps
      } = props as IConnectedControlHandlerProps;

      // Compare the props without the control handlers.
      // Note that `previousFinalProps` is never `undefined`, so `previousProps` is used as a proxy here.
      const havePropsChanged = !previousFinalProps || areShallowDifferent(previousFinalProps, finalProps, { debug: true });

      const selector = !previousSelector || havePropsChanged ?
        bindItemCacheSelectorToProps(mapItemCacheToProps, finalProps as TOwnProps) :
        previousSelector;

      const {
        result,
        demands,
        dependencies
      } = itemCacheEvaluator.evaluate({
        selector,
        controlHandlers,
        state,
        owner,
        suspender: owner
      });

      previousFinalProps = finalProps as TOwnProps;
      previousSelector = selector;

      return {
        ...(result as {}),
        itemCacheDemanderDemands: demands,
        itemCacheDemanderDependencies: dependencies,
        itemCacheDemanderDispatch: dispatch
      } as TInjectedProps & IItemCacheDemanderProps;
    };
  };
}
